import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { FormResponse, FormId, CompactFormResponse } from '@business/entities/form';
import { FormAPI } from '@business/api/form';
import { pipeIf } from '@core/utils/rxjs/operators';

export class FormBloc extends BaseBloc {
  private readonly formRepo = new Repository<FormResponse | CompactFormResponse>({
    getItemId: form => form.id,
  });

  readonly forms$ = this.formRepo.items$;
  readonly selectForm = this.formRepo.selectItem;

  onReset() {
    this.formRepo.reset();
  }

  fetchForms = () => {
    return FormAPI.fetchForms().pipe(this.formRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_form_error') }));
  };

  fetchForm = (id: FormId) => {
    return FormAPI.fetchForm(id).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_form_error') }),
    );
  };

  createForm = (data: Partial<FormResponse>) => {
    return FormAPI.createForm(data).pipe(
      this.formRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_form'),
      }),
    );
  };

  updateForm = (id: FormId, data: Partial<FormResponse>) => {
    return FormAPI.updateForm(id, data).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_form'),
      }),
    );
  };

  deleteForm = (id: FormId) => {
    return showDeleteConfirmation('Delete Form', 'Do you really want to remove this form?').pipe(
      switchMap(() => FormAPI.deleteForm(id)),
      this.formRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_form'),
      }),
    );
  };

  duplicateForm = (id: FormId, form: Partial<FormResponse>) => {
    return FormAPI.duplicateForm(id, form).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('clone_form'),
      }),
    );
  };

  saveFormFields = (id: FormId, data: Pick<FormResponse, 'fields'>, message = true) => {
    return FormAPI.saveFormFields(id, data).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      pipeIf(
        message &&
          handleMessage({
            type: requestMessage('save_form_fields'),
          }),
      ),
    );
  };

  deleteFormShare = (id: string) => {
    return FormAPI.deleteFormShare(id);
  };
}
